import React, { useEffect, useRef, useState } from 'react';

import { Box, Group, Loader, Transition } from '@mantine/core';
import { IconCircleCheck, IconCircleDashed } from '@tabler/icons-react';
import LevelPicker from 'components/LevelPicker/LevelPicker';
import type { WorkoutMovementWithScoring } from 'components/Workout/logging';
import { DistanceUnit, MovementResult, ScoringTypeInput, WeightUnit } from 'components/Workout/ScoringTypes';
import { WorkoutLevelEnum } from 'types/enums';

type SaveStatus = 'idle' | 'dirty' | 'saving' | 'saved';

// Pops in the icon on mount — used with key={status} to animate each state change.
const PopIn = ({ children }: { children: React.ReactNode }): JSX.Element => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    setVisible(true);
  }, []);
  return (
    <Box
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'scale(1)' : 'scale(0.6)',
        transition: 'opacity 180ms ease, transform 180ms ease',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      {children}
    </Box>
  );
};

const SaveStatusIndicator = ({ status }: { status: SaveStatus }): JSX.Element => {
  // Keep the last non-idle status so the icon doesn't change during the exit fade.
  const lastVisible = useRef<SaveStatus>('dirty');
  if (status !== 'idle') lastVisible.current = status;

  return (
    <Transition
      mounted={status !== 'idle'}
      transition='fade'
      duration={250}
      exitDuration={600}
      timingFunction='ease'
    >
      {styles => (
        <Box style={{ ...styles, display: 'flex', alignItems: 'center' }}>
          <PopIn key={lastVisible.current}>
            {lastVisible.current === 'saving' && <Loader size='xs' />}
            {lastVisible.current === 'saved' && <IconCircleCheck size={16} color='green' />}
            {lastVisible.current === 'dirty' && <IconCircleDashed size={16} color='gray' />}
          </PopIn>
        </Box>
      )}
    </Transition>
  );
};

interface WhiteboardCellProps {
  movement: WorkoutMovementWithScoring;
  value: MovementResult | undefined;
  onChange: (result: MovementResult) => void;
  onCommit: (result: MovementResult) => Promise<void>;
  enlarged?: boolean;
  hasLevels?: boolean;
  level?: WorkoutLevelEnum | null;
  levelScaled?: boolean;
  levelRx?: boolean;
  levelRxPlus?: boolean;
  onLevelChange?: (level: WorkoutLevelEnum) => void;
  onLevelCommit?: (level: WorkoutLevelEnum) => Promise<void>;
}

const WhiteboardCell = ({
  movement,
  value,
  onChange,
  onCommit,
  enlarged = false,
  hasLevels = false,
  level = null,
  levelScaled = false,
  levelRx = false,
  levelRxPlus = false,
  onLevelChange,
  onLevelCommit,
}: WhiteboardCellProps): JSX.Element | null => {
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');

  const { scoringType } = movement;
  const weightUnit: WeightUnit = movement.weightUnit === 'kg' ? 'kg' : 'lbs';
  const distanceUnit: DistanceUnit =
    movement.distanceUnit === 'km' ? 'km' : movement.distanceUnit === 'mi' ? 'mi' : 'm';
  const targetReps = movement.scoreSets.map(s => s.targetReps);

  if (!scoringType) return null;

  const handleChange = (next: MovementResult): void => {
    setSaveStatus('dirty');
    onChange(next);
  };

  // Levels commit immediately on tap (no blur-gating like the scoring input above) —
  // optimistic update, reverted if the bulk-set request fails.
  const handleLevelPick = (nextLevel: WorkoutLevelEnum): void => {
    const previous = level;
    onLevelChange?.(nextLevel);
    onLevelCommit?.(nextLevel).catch(() => {
      if (previous !== null) onLevelChange?.(previous);
    });
  };

  return (
    <Box
      onBlur={e => {
        if (!e.currentTarget.contains(e.relatedTarget as Node) && value && saveStatus === 'dirty') {
          setSaveStatus('saving');
          onCommit(value)
            .then(() => {
              setSaveStatus('saved');
              setTimeout(() => setSaveStatus('idle'), 2000);
            })
            .catch(() => setSaveStatus('dirty'));
        }
      }}
    >
      {/* Reserved even when this section has no levels, so every column's scoring input
          lines up at the same row regardless of which cells have a picker above them. */}
      <Box mb={6} sx={{ minHeight: enlarged ? 42 : 36, display: 'flex', justifyContent: 'center' }}>
        {hasLevels && (
          <LevelPicker
            size={enlarged ? 'md' : 'sm'}
            value={level}
            onChange={handleLevelPick}
            levelScaled={levelScaled}
            levelRx={levelRx}
            levelRxPlus={levelRxPlus}
          />
        )}
      </Box>
      <Group spacing='xs' noWrap align='center'>
        <Box sx={{ flex: 1 }}>
          <ScoringTypeInput
            scoringType={scoringType}
            value={value}
            onChange={handleChange}
            weightUnit={weightUnit}
            distanceUnit={distanceUnit}
            targetReps={targetReps}
            enlarged={enlarged}
          />
        </Box>
        <SaveStatusIndicator status={saveStatus} />
      </Group>
    </Box>
  );
};

export default WhiteboardCell;
